import { Data, Utils } from 'common';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import reduce from 'lodash/reduce';

const {
	CalculatedData,
	Carrier,
	Capability,
	InternalCapability,
	ServiceType,
	PackageType,
} = Data;

const { Session, Util } = Utils;

class StaticRateData {
	static generateStaticData = (serviceRates, upsGroundETA) => {
		const carrierCapabilities = Session.getCarrierCapabilities();
		const canPrintUSPSReturnServicePrepaid = Session.hasCapability(
			Capability.UspsReturnServicePrepaid
		);
		const canPrintInstaLabel = Session.hasCapability(Capability.Instalabel);
		const canPrintUPS =
			(Session.hasCapability(Capability.Ups) &&
				Session.hasCapability(InternalCapability.DisplayUpsByDefault)) ||
			carrierCapabilities[Carrier.UPS] === true;

		const canPrintUPSReturns = Session.hasCapability(Capability.UpsReturns);
		const isUPSShowService = canPrintUPS && canPrintUPSReturns;
		const isFreemiumUser = Session.isFreemiumUser();
		const showConsolidatedReturns = canPrintInstaLabel && isFreemiumUser;
		const canPrintFcPresort = Session.hasCapability(Capability.FcGap);
		const canPrintPsPresort = Session.hasCapability(Capability.PsGap);

		const uspsPayOnUseReturnServiceRate = serviceRates.filter(
			rate =>
				rate.serviceType === ServiceType.UspsPayOnUseReturn &&
				rate.packagingType === PackageType.Package
		)[0];

		const upsGroundserviceRate = serviceRates.filter(
			rate =>
				rate.serviceType === ServiceType.UpsGround &&
				rate.packagingType === PackageType.Package
		)[0];

		const staticData = [];

		if (isUPSShowService || showConsolidatedReturns) {
			staticData.push(
				{
					carrier: Carrier.UPS,
					isFetching: false,
					isStaticRate: true,
					serviceTitle: 'UPS Ground Returns (1-5 Days)',
					key: 'UpsgroundPrintAtHomeLabel',
					packageDescription: `<b>UPS Drop-Off: Print-at-Home Label</b><br><br>
						A return label will be emailed to your recipient for printing.
						The return can be dropped-off at any UPS drop-off location.
						<br><br><i>Label must be printed within 24 hours.</i>`,
					packageName: 'UPS Drop-Off: Print-at-Home Label',
					packageType: PackageType.PrintAtHomeLabel,
					serviceName: 'UPS Ground Returns',
					serviceType: ServiceType.UpsGround,
					serviceTypeDescription:
						'Get your package in about in 1 to 5 days after shipment.',
					value: 'UPS Drop-Off: Print-at-Home Label',
					shipmentCost: upsGroundserviceRate?.shipmentCost,
					estimatedDeliveryDate: upsGroundserviceRate?.estimatedDeliveryDate,
					estimatedDeliveryDays: upsGroundserviceRate?.estimatedDeliveryDays,
					packageTypeDescription: `A return label with instructions will be emailed to your recipient for printing.
						Return can be dropped-off at any UPS Store or Drop Box.
						<br><i>Label must be printed within 24 hours.</i>`,
				},
				{
					carrier: Carrier.UPS,
					isFetching: false,
					isStaticRate: true,
					serviceTitle: 'UPS Ground Returns (1-5 Days)',
					key: 'UpsGroundDropOffNoLabelNeeded',
					packageDescription: `<b>UPS Drop-Off: No Label Needed</b><br><br>
						<b>No printer required!</b> For this return, a QR code and
						instructions will be emailed to your recipient. The recipient drops off the
						package at The UPS Store® by presenting the email (on their phone or
						printed-out). For further flexibility, the email includes a print-at-home option.`,
					packageName: 'UPS Drop-Off: No Label Needed',
					packageType: PackageType.DropOffNoLabelNeeded,
					serviceName: 'UPS Ground Returns',
					serviceType: ServiceType.UpsGround,
					serviceTypeDescription:
						'Get your package in about 2 days after shipment.',
					value: 'UPS Drop-Off: No Label Needed',
					shipmentCost: upsGroundserviceRate?.shipmentCost,
					estimatedDeliveryDate: upsGroundserviceRate?.estimatedDeliveryDate,
					estimatedDeliveryDays: upsGroundserviceRate?.estimatedDeliveryDays,
					packageTypeDescription: `<b>No printer needed!</b> A QR code for the return will be emailed to your recipient.
						Return can be dropped off at The UPS Store®.
						<br><i>QR code valid for 90 days.</i>`,
				}
			);
		}

		if (
			!canPrintUSPSReturnServicePrepaid &&
			canPrintInstaLabel &&
			!isFreemiumUser
		) {
			const serviceData = serviceRates.filter(
				serviceRate =>
					serviceRate?.serviceType ===
						('usps_first_class_mail' && canPrintFcPresort) ||
					serviceRate?.serviceType === 'usps_ground_advantage' ||
					serviceRate?.serviceType === 'usps_priority_mail' ||
					serviceRate?.serviceType === 'usps_priority_mail_express' ||
					serviceRate?.serviceType ===
						('usps_parcel_select' && canPrintPsPresort) ||
					serviceRate?.serviceType === 'usps_media_mail'
			);

			// Ground Advantage rates are returned after the other rates - move it up to the top
			const groundAdvantageIndex = serviceData.findIndex(
				serviceRate => serviceRate.serviceType === 'usps_ground_advantage'
			);

			if (groundAdvantageIndex !== -1) {
				const groundAdvantage = serviceData.splice(groundAdvantageIndex, 1);
				serviceData.unshift(groundAdvantage[0]);
			}

			const serviceDefaultRates = [
				{
					serviceType: 'usps_first_class_mail',
					deliveryDays: '1-3',
					serviceTitle: 'First-Class Mail (1 - 3 Days)',
					key: 'USPSFirstClassMail',
					value: 'USPS FCM',
				},
				{
					serviceType: 'usps_ground_advantage',
					deliveryDays: '1-3',
					serviceTitle: 'Ground Advantage (1 - 3 Days)',
					key: 'UspsGroundAdvantage',
					value: 'USPS GA',
				},
				{
					serviceType: 'usps_priority_mail',
					deliveryDays: '1-3',
					serviceTitle: 'Priority Mail (1 - 3 Days)',
					key: 'USPSPriorityMail',
					value: 'USPS PM',
				},
				{
					serviceType: 'usps_priority_mail_express',
					deliveryDays: '1-2',
					serviceTitle: 'Priority Mail Express (1 - 2 Days)',
					key: 'USPSPriorityMailExpress',
					value: 'USPS PME',
				},
				{
					serviceType: 'usps_parcel_select',
					deliveryDays: '2-9',
					serviceTitle: 'Parcel Select Ground (2 - 9 Days)',
					key: 'USPSParcelSelectGround',
					value: 'USPS PSG',
				},
				{
					serviceType: 'usps_media_mail',
					deliveryDays: '2-9',
					serviceTitle: 'Media Mail (2 - 9 Days)',
					key: 'USPSMediaMail',
					value: 'USPS MM',
				},
			];

			serviceData.forEach(service => {
				const serviceRate = serviceDefaultRates.find(
					serviceDefaultRate =>
						serviceDefaultRate.serviceType === service.serviceType
				);

				staticData.push(
					{
						carrier: Carrier.USPS,
						isFetching: false,
						isStaticRate: true,
						serviceTitle: serviceRate.value,
						key: `${serviceRate.key}PrintAtHomeLabel`,
						packageDescription: `<b>USPS Print-at-Home Label</b><br />
							For this return, a label will be emailed to your recipient for printing. The return can be dropped off at any Post Office or given to a mail carrier.<br />
							<i>Label must be printed within 24 hours.</i>`,
						packageName: 'Print-at-Home Label',
						packageType: PackageType.PrintAtHomeLabel,
						serviceName: serviceRate.serviceTitle,
						serviceType: service.serviceType,
						shipmentCost: service?.shipmentCost,
						estimatedDeliveryDate: serviceRate?.deliveryDays,
						estimatedDeliveryDays: '',
						serviceTypeDescription:
							'Get your package in about 2 days after shipment.',
						value: `${serviceRate.value} - Print-at-Home Label`,
						packageTypeDescription: `A return label will be emailed to your recipient for printing.
							Return can be dropped off at any Post Office or given to a mail carrier.
							<br><i>Label must be printed within 24 hours.</i>`,
					},
					{
						carrier: Carrier.USPS,
						isFetching: false,
						isStaticRate: true,
						serviceTitle: serviceRate.value,
						key: `${serviceRate.key}DropOffNoLabelNeeded`,
						packageDescription: `<b>USPS Drop-Off: No Label Needed</b><br />
							<b>No printer required!</b> For this return, a QR code and instructions will be emailed to your recipient. The recipient drops off their return at the Post Office by presenting the email (on their phone or printed out). For further flexibility, the email includes a print-at-home option.<br />
							<i>QR code valid for 90 days.</i>`,
						packageName: 'Drop-Off: No Label Needed',
						packageType: PackageType.DropOffNoLabelNeeded,
						serviceName: serviceRate.serviceTitle,
						serviceType: service.serviceType,
						shipmentCost: service?.shipmentCost,
						estimatedDeliveryDate: serviceRate?.deliveryDays,
						estimatedDeliveryDays: '',
						serviceTypeDescription:
							'Get your package in about 2 days after shipment.',
						value: `${serviceRate.value} - Drop-Off: No Label Needed`,
						packageTypeDescription: `<b>No printer needed!</b> A QR code for the return will be emailed to your recipient.
							Return can be dropped off at any Post Office.
							<br><i>QR code valid for 90 days.</i>`,
					}
				);
			});
		} else if (canPrintUSPSReturnServicePrepaid) {
			staticData.push({
				carrier: Carrier.USPS,
				isFetching: false,
				isStaticRate: true,
				serviceTitle: 'USPS Returns (2 Days)',
				key: 'UspsPayOnUseReturnPrintAtHomeLabel',
				packageDescription: `<b>USPS Print-at-Home Label</b><br />
					A return label will be emailed to your recipient for printing. The return can be dropped off at any Post Office or given to a mail carrier.<br />
					<i>Label must be printed within 24 hours.</i>`,
				packageName: 'Print-at-Home Label',
				serviceName: 'USPS Returns',
				packageType: PackageType.PrintAtHomeLabel,
				serviceType: ServiceType.UspsPayOnUseReturn,
				serviceTypeDescription:
					'Get your package in about 2 days after shipment.',
				value: 'Print-at-Home Label',
				shipmentCost: uspsPayOnUseReturnServiceRate?.shipmentCost,
				estimatedDeliveryDate:
					uspsPayOnUseReturnServiceRate?.estimatedDeliveryDate,
				estimatedDeliveryDays:
					uspsPayOnUseReturnServiceRate?.estimatedDeliveryDays,
				packageTypeDescription: `A return label will be emailed to your recipient for printing.
					Return can be dropped off at any Post Office or given to a mail carrier.
					<br><i>Label must be printed within 24 hours.</i>`,
			});
			if (canPrintInstaLabel) {
				staticData.push({
					carrier: Carrier.USPS,
					isFetching: false,
					isStaticRate: true,
					serviceTitle: 'USPS Returns (2 Days)',
					key: 'UspsPayOnUseReturnDropOffNoLabelNeeded',
					packageDescription: `<b>USPS Drop-Off: No Label Needed</b><br />
						<b>No printer required!</b> For this return, a QR code and instructions will be emailed to your recipient. The recipient drops off their return at the Post Office by presenting the email (on their phone or printed out). For further flexibility, the email includes a print-at-home option.<br />
						<i>QR code valid for 90 days.</i>`,
					packageName: 'Drop-Off: No Label Needed',
					serviceName: 'USPS Returns',
					packageType: PackageType.DropOffNoLabelNeeded,
					serviceType: ServiceType.UspsPayOnUseReturn,
					serviceTypeDescription:
						'Get your package in about 2 days after shipment.',
					value: 'Drop-Off: No Label Needed',
					shipmentCost: uspsPayOnUseReturnServiceRate?.shipmentCost,
					estimatedDeliveryDate:
						uspsPayOnUseReturnServiceRate?.estimatedDeliveryDate,
					estimatedDeliveryDays:
						uspsPayOnUseReturnServiceRate?.estimatedDeliveryDays,
					packageTypeDescription: `<b>No printer needed!</b> A QR code for the return will be emailed to your recipient.
						Return can be dropped off at any Post Office.
						<br><i>QR code valid for 90 days.</i>`,
				});
			}
		} else if (!showConsolidatedReturns) {
			return CalculatedData.processRates(serviceRates, upsGroundETA);
		}

		return staticData;
	};

	/**
	 * Process the response from /rates api and merge the rates into staticData.
	 * @param {array} serviceRates Data response received from rates api.
	 * @returns {array}
	 */
	static processRates = (rates, upsGroundETA) => {
		const carrierCapabilities = Session.getCarrierCapabilities();
		const serviceRates = this.generateStaticData(
			Util.toCamelCase(rates),
			upsGroundETA
		);

		return reduce(
			serviceRates,
			(_result, rate) => {
				// get the Service static data
				const serviceStaticData = rate;

				/**
				 * Dev note:
				 * Exclude the following rates:
				 * 1. Any service that is not included in Data module
				 * 2. Any service that is not enabled for user's account
				 */
				if (
					!isObject(serviceStaticData) ||
					(serviceStaticData.carrier === Carrier.UPS &&
						!carrierCapabilities[Carrier.UPS]) ||
					(serviceStaticData.carrier === Carrier.DHLExpress &&
						!carrierCapabilities[Carrier.DHLExpress]) ||
					(serviceStaticData.carrier === Carrier.GlobalPost &&
						!carrierCapabilities[Carrier.GlobalPost])
				) {
					return _result;
				}

				// e.g.
				// estimatedDeliveryDays = "1-2" => [1, 2]
				// estimatedDeliveryDays = "1"   => [1, undefined]
				// estimatedDeliveryDays = null  => [1, 999]
				const estimatedDeliveryDays = isString(rate.estimatedDeliveryDays)
					? rate.estimatedDeliveryDays.match(/\d+/g)?.map(Number)
					: [1, 999];

				let [minDeliverDays, maxDeliverDays] = isArray(estimatedDeliveryDays)
					? estimatedDeliveryDays
					: [];

				// update estimated delivery days of 'ups ground'
				if (rate.serviceType === ServiceType.UpsGround && upsGroundETA) {
					rate.estimatedDeliveryDays =
						isString(upsGroundETA) && upsGroundETA !== ''
							? upsGroundETA
							: rate.estimatedDeliveryDays;
					minDeliverDays = parseInt(upsGroundETA);
					maxDeliverDays = null;
				}

				_result.push({
					// service static data
					...serviceStaticData,

					// service rate data from server
					...rate,

					// service ETA
					minDeliverDays,
					maxDeliverDays,
				});

				return _result;
			},
			[]
		);
	};
}

export default StaticRateData;
